/**
 * PlatformShowcaseSection — visual gallery of Nexus Design AI core capabilities.
 * Adds warmth and color to the otherwise monochrome Swiss aesthetic, while
 * remaining on-brand (minimal text, confident visuals, subtle motion).
 */
import { motion } from 'framer-motion';
import { Sparkles, Mic, Headset, Box, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';

const TILES = [
  {
    slug: 'ai-engines',
    image: '/images/gallery/ai_engines.webp',
    icon: Sparkles,
    accent: 'from-blue-500/40 via-violet-500/20 to-transparent',
    dot: 'bg-cyan-400',
    title: { en: 'Traceable Review Gates', tr: 'İzlenebilir İnceleme Kapıları' },
    subtitle: { en: 'Deterministic checks before claims', tr: 'İddialardan önce deterministik kontroller' },
    body: {
      en: 'Unit checks · BOM reconciliation · evidence labels · visible assumptions · fail-closed safety gates · human sign-off — designed to make preliminary outputs reviewable rather than present them as final engineering.',
      tr: 'Birim kontrolleri · BOM uzlaştırma · kanıt etiketleri · görünür varsayımlar · fail-closed güvenlik kapıları · insan onayı — ön çıktıları nihai mühendislik gibi sunmak yerine incelenebilir kılmak için tasarlanmıştır.',
    },
    cta: { en: 'Review the workflow', tr: 'Doğrulama akışını incele' },
    link: '/case-studies',
  },
  {
    slug: '3d-modeling',
    image: '/images/gallery/3d_modeling.webp',
    icon: Box,
    accent: 'from-cyan-500/40 via-blue-500/20 to-transparent',
    dot: 'bg-blue-400',
    title: { en: 'Text-to-3D', tr: 'Metin → 3D' },
    subtitle: { en: 'From natural language to manufacturing-oriented geometry', tr: 'Doğal dilden üretim-yönelimli geometriye' },
    body: {
      en: 'Describe a product — primitives, a robotic manipulator, a consumer device — and generate a preliminary 3D concept when the required model-generation service and sufficient source data are available. Processing time varies.',
      tr: 'Bir ürünü tanımlayın — temel geometriler, robotik manipülatör, tüketici cihazı — ve gerekli model-üretim servisi ile yeterli kaynak verisi mevcut olduğunda ön 3D konsept oluşturun. İşlem süresi değişkendir.',
    },
    cta: { en: 'Open Design Studio', tr: 'Design Studio\'yu aç' },
    link: '/#studio',
  },
  {
    slug: 'voice-commands',
    image: '/images/gallery/voice_commands.webp',
    icon: Mic,
    accent: 'from-violet-500/40 via-fuchsia-500/20 to-transparent',
    dot: 'bg-violet-400',
    title: { en: 'Voice-Commanded Design', tr: 'Sesli Komutla Tasarım' },
    subtitle: { en: 'Voice-to-brief prototype; review before applying.', tr: 'Sesten tasarım briefine; uygulamadan önce inceleyin.' },
    body: {
      en: '"Increase the base height by 10 mm." Nexus can transcribe the request and propose a structured parameter change for review. Automatic CAD-driver updates depend on a validated integration.',
      tr: '"Tabanın yüksekliğini 10 mm artır." Nexus isteği yazıya döküp inceleme için yapılandırılmış bir parametre değişikliği önerebilir. Otomatik CAD sürücüsü güncellemesi doğrulanmış entegrasyona bağlıdır.',
    },
    cta: { en: 'Try Voice Engine', tr: 'Ses Motorunu dene' },
    link: '/engines#voice-design',
  },
  {
    slug: 'ar-integration',
    image: '/images/gallery/ar_integration.webp',
    icon: Headset,
    accent: 'from-emerald-500/40 via-cyan-500/20 to-transparent',
    dot: 'bg-emerald-400',
    title: { en: 'AR Field Integration', tr: 'AR Saha Entegrasyonu' },
    subtitle: { en: 'Mobile/tablet AR beta; smart-glasses roadmap', tr: 'Mobil/tablet AR beta; akıllı gözlük yol haritası' },
    body: {
      en: 'The mobile/tablet beta can draft image-assisted fault observations and repair guidance for review. Smart-glasses overlays, live parts data and field validation remain roadmap or integration-dependent.',
      tr: 'Mobil/tablet beta, inceleme için görüntü destekli arıza gözlemleri ve onarım rehberi taslağı üretebilir. Akıllı gözlük katmanları, canlı parça verisi ve saha doğrulaması yol haritasında veya entegrasyona bağlıdır.',
    },
    cta: { en: 'See AR use-cases', tr: 'AR kullanımlarını gör' },
    link: '/#applications',
  },
];

export default function PlatformShowcaseSection() {
  const { language } = useLanguage();
  const isTr = language === 'tr';

  return (
    <section className="relative py-24 md:py-32 bg-[#060710] overflow-hidden" data-testid="platform-showcase">
      {/* Ambient color glows — adds warmth without breaking Swiss minimalism */}
      <div className="absolute inset-0 pointer-events-none opacity-60">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-blue-500/10 blur-[160px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full bg-violet-500/10 blur-[140px]" />
        <div className="absolute top-1/2 left-0 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[120px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="mb-14 max-w-2xl">
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-cyan-300/80 mb-3">
            {isTr ? '// PLATFORM — YETENEKLER' : '// PLATFORM — CAPABILITIES'}
          </div>
          <h2 className="text-4xl md:text-5xl font-light text-white leading-tight mb-4">
            {isTr ? (
              <>Dört yetenek.<br /><span className="text-white/50">Tek orkestrasyon.</span></>
            ) : (
              <>Four capabilities.<br /><span className="text-white/50">One orchestration.</span></>
            )}
          </h2>
          <p className="text-base text-white/60 leading-relaxed">
            {isTr
              ? 'Nexus Design AI, ön çıktıları birim, BOM, kanıt ve insan onayı kapılarından geçiren izlenebilir bir inceleme iş akışı sunar.'
              : 'Nexus Design AI provides a traceable review workflow with unit, BOM, evidence and human sign-off gates for preliminary outputs.'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {TILES.map((tile, idx) => {
            const Icon = tile.icon;
            return (
              <motion.div
                key={tile.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6, delay: idx * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                className="group relative overflow-hidden border border-white/10 bg-[#0A0C14]"
                data-testid={`showcase-tile-${tile.slug}`}
              >
                {/* Image with accent gradient overlay */}
                <div className="relative aspect-[16/10] overflow-hidden bg-black">
                  <img
                    src={tile.image}
                    alt={tile.title[language] || tile.title.en}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${tile.accent} mix-blend-screen opacity-70`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C14] via-[#0A0C14]/40 to-transparent" />
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${tile.dot} shadow-[0_0_12px_currentColor]`} />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-white/70">
                      {`0${idx + 1}`.slice(-2)}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4">
                    <Icon className="w-6 h-6 text-white/90" strokeWidth={1.5} />
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 md:p-7">
                  <div className="text-xs font-mono uppercase tracking-widest text-white/40 mb-1">
                    {tile.subtitle[language] || tile.subtitle.en}
                  </div>
                  <h3 className="text-2xl font-light text-white mb-3">
                    {tile.title[language] || tile.title.en}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed mb-5">
                    {tile.body[language] || tile.body.en}
                  </p>
                  {tile.link.startsWith('#') || tile.link.includes('#') ? (
                    <a
                      href={tile.link}
                      className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/80 hover:text-white group/cta transition-colors"
                    >
                      {tile.cta[language] || tile.cta.en}
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/cta:translate-x-1" />
                    </a>
                  ) : (
                    <Link
                      to={tile.link}
                      className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-white/80 hover:text-white group/cta transition-colors"
                    >
                      {tile.cta[language] || tile.cta.en}
                      <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/cta:translate-x-1" />
                    </Link>
                  )}
                </div>

                {/* Subtle corner accent on hover */}
                <div className={`absolute top-0 right-0 w-32 h-px bg-gradient-to-l ${tile.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
